import { useState, useEffect, useRef } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { searchApiRef } from '@backstage/plugin-search-react';
import { SearchResultItem, EntityKind } from './types';

export function useHeaderSearch(query: string): {
  results: SearchResultItem[];
  totalCount: number;
  loading: boolean;
} {
  const searchApi = useApi(searchApiRef);
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (query.trim().length < 1) {
      setResults([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    let cancelled = false;

    timeoutRef.current = setTimeout(async () => {
      try {
        const response = await searchApi.query({
          term: query,
          // Include both the entity catalog and published TechDocs pages so
          // documentation content is surfaced directly in the header dropdown.
          types: ['software-catalog', 'techdocs'],
        });

        if (cancelled) return;

        // Keep up to 5 catalog entries and 3 doc pages so the dropdown stays
        // compact. The footer "View all N results" link leads to /search for
        // the full result list.
        const catalogHits = response.results
          .filter(r => r.type === 'software-catalog')
          .slice(0, 5);
        const docsHits = response.results
          .filter(r => r.type === 'techdocs')
          .slice(0, 3);

        const mapped: SearchResultItem[] = [
          ...catalogHits.map(r => {
            const doc = r.document as unknown as Record<string, string>;
            const kind = (doc.kind || 'component').toLowerCase() as EntityKind;
            const name = doc.name || doc.title || '';
            const namespace = doc.namespace || 'default';
            const description = doc.description || doc.text || '';
            let link: string;
            if (kind === 'template') {
              link = `/create/templates/${namespace}/${name}`;
            } else {
              link = `/catalog/${namespace}/${kind}/${name}`;
            }
            return { kind, name, description, namespace, link };
          }),
          ...docsHits.map(r => {
            // TechDocs documents use `title` (page heading) and `location`
            // (the routed URL within the docs site) instead of the catalog
            // entity fields. We surface the page title as the result name and
            // the entity name as a context hint in the description.
            const doc = r.document as unknown as Record<string, string>;
            const title = doc.title || doc.name || 'Documentation';
            const entityName = doc.name || '';
            const entityKind = doc.kind || 'Component';
            const namespace = doc.namespace || 'default';
            const location =
              doc.location ||
              `/docs/${namespace}/${entityKind.toLowerCase()}/${entityName}`;
            return {
              kind: 'docs' as EntityKind,
              name: title,
              description: entityName
                ? `${entityKind} · ${entityName}`
                : '',
              namespace,
              link: location,
            };
          }),
        ];

        setResults(mapped);
        setTotalCount(response.numberOfResults ?? response.results.length);
        setLoading(false);
      } catch {
        if (!cancelled) {
          setResults([]);
          setTotalCount(0);
          setLoading(false);
        }
      }
    }, 150);

    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query, searchApi]);

  return { results, totalCount, loading };
}
